/* =======================================================================
   PANDISWELT HERO SLIDER
   Eigenstaendige Slider-Sektion, isoliert vom Hero-Banner.

   Custom Element: <pandiswelt-hero-slider>

   Features:
     - Auto-Play konfigurierbar (data-autoplay, data-autoplay-duration)
     - Pause bei Hover (Desktop) konfigurierbar (data-pause-on-hover)
     - Touch-Wischen auf Mobile
     - Tastatur-Navigation (Pfeiltasten)
     - Pfeile + Dots klickbar
     - Reduced-Motion respektiert: kein Auto-Play, sofortiges Snap
     - Wrap-around (vom letzten zum ersten Slide)
     - Nach einem Wischen wird ein versehentlicher Klick unterdrueckt
     - Unsichtbare Slides sind per inert fuer Klick und Tastatur gesperrt
   ======================================================================= */

(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class PandisweltHeroSlider extends HTMLElement {
    constructor() {
      super();

      this.track            = null;
      this.slides           = [];
      this.dots             = [];
      this.currentIndex     = 0;

      this.autoplayEnabled  = false;
      this.autoplayDuration = 6000;
      this.autoplayTimer    = null;
      this.pauseOnHover     = true;
      this.isPaused         = false;

      this.touchStartX      = 0;
      this.touchEndX        = 0;
      this.justSwiped       = false;

      this._onPrev          = this._onPrev.bind(this);
      this._onNext          = this._onNext.bind(this);
      this._onDot           = this._onDot.bind(this);
      this._onTouchStart    = this._onTouchStart.bind(this);
      this._onTouchEnd      = this._onTouchEnd.bind(this);
      this._onKeyDown       = this._onKeyDown.bind(this);
      this._onPointerEnter  = this._onPointerEnter.bind(this);
      this._onPointerLeave  = this._onPointerLeave.bind(this);
      this._onTrackClick    = this._onTrackClick.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-hslider-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('.pandiswelt-hslider__slide'));
      if (this.slides.length === 0) return;

      this.dots = Array.from(this.querySelectorAll('[data-hslider-dot]'));

      this.autoplayEnabled  = this.dataset.autoplay === 'true' && this.slides.length >= 2;
      this.autoplayDuration = (parseFloat(this.dataset.autoplayDuration) || 6) * 1000;
      this.pauseOnHover     = this.dataset.pauseOnHover === 'true';

      /* Buttons + Dots binden */
      const prevBtn = this.querySelector('[data-hslider-prev]');
      const nextBtn = this.querySelector('[data-hslider-next]');
      if (prevBtn) prevBtn.addEventListener('click', this._onPrev);
      if (nextBtn) nextBtn.addEventListener('click', this._onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this._onDot));

      /* Touch-Wischen nur sinnvoll bei mehreren Slides */
      if (this.slides.length >= 2) {
        this.track.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.track.addEventListener('touchend', this._onTouchEnd, { passive: true });
        this.track.addEventListener('click', this._onTrackClick, true);

        /* Tastatur */
        this.setAttribute('tabindex', '0');
        this.addEventListener('keydown', this._onKeyDown);

        /* Hover-Pause */
        if (this.pauseOnHover) {
          this.addEventListener('pointerenter', this._onPointerEnter);
          this.addEventListener('pointerleave', this._onPointerLeave);
        }
      }

      /* Reduced Motion: kein Auto-Play */
      if (prefersReducedMotion()) {
        this.autoplayEnabled = false;
      }

      /* Initial-Position setzen, dann ggf. Auto-Play starten */
      this._goToSlide(0, false);
      if (this.autoplayEnabled) {
        this._startAutoplay();
      }
    }

    disconnectedCallback() {
      this._stopAutoplay();

      const prevBtn = this.querySelector('[data-hslider-prev]');
      const nextBtn = this.querySelector('[data-hslider-next]');
      if (prevBtn) prevBtn.removeEventListener('click', this._onPrev);
      if (nextBtn) nextBtn.removeEventListener('click', this._onNext);
      this.dots.forEach((dot) => dot.removeEventListener('click', this._onDot));

      if (this.track) {
        this.track.removeEventListener('touchstart', this._onTouchStart);
        this.track.removeEventListener('touchend', this._onTouchEnd);
        this.track.removeEventListener('click', this._onTrackClick, true);
      }
      this.removeEventListener('keydown', this._onKeyDown);
      this.removeEventListener('pointerenter', this._onPointerEnter);
      this.removeEventListener('pointerleave', this._onPointerLeave);
    }

    /* ---------- Navigation ---------- */

    _goToSlide(index, withTransition) {
      if (!this.slides || this.slides.length === 0) return;
      const total = this.slides.length;
      const newIndex = ((index % total) + total) % total;
      this.currentIndex = newIndex;

      if (withTransition === false) {
        this.track.style.transition = 'none';
        /* force reflow */
        // eslint-disable-next-line no-unused-expressions
        this.track.offsetHeight;
      }
      this.track.style.transform = 'translateX(-' + (newIndex * 100) + '%)';
      if (withTransition === false) {
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }

      this.slides.forEach((slide, i) => {
        const isActive = i === newIndex;
        slide.classList.toggle('is-active', isActive);
        /* Unsichtbare Slides nicht anklickbar und nicht per Tab erreichbar */
        slide.inert = !isActive;
        if (isActive) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
      this.dots.forEach((dot, i) => {
        const isActive = i === newIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    _onPrev() {
      this._goToSlide(this.currentIndex - 1, true);
      this._restartAutoplay();
    }

    _onNext() {
      this._goToSlide(this.currentIndex + 1, true);
      this._restartAutoplay();
    }

    _onDot(event) {
      const idx = parseInt(event.currentTarget.dataset.hsliderDot, 10) - 1;
      if (isNaN(idx)) return;
      this._goToSlide(idx, true);
      this._restartAutoplay();
    }

    /* ---------- Touch ---------- */

    _onTouchStart(event) {
      if (event.touches && event.touches.length > 0) {
        this.touchStartX = event.touches[0].clientX;
      }
    }

    _onTouchEnd(event) {
      if (event.changedTouches && event.changedTouches.length > 0) {
        this.touchEndX = event.changedTouches[0].clientX;
        const diff = this.touchStartX - this.touchEndX;
        if (Math.abs(diff) < 40) return;
        this.justSwiped = true;
        window.setTimeout(() => { this.justSwiped = false; }, 350);
        if (diff > 0) {
          this._onNext();
        } else {
          this._onPrev();
        }
      }
    }

    /* Klick direkt nach einem Wischen verwerfen */
    _onTrackClick(event) {
      if (this.justSwiped) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    /* ---------- Tastatur ---------- */

    _onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this._onPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this._onNext();
      }
    }

    /* ---------- Hover-Pause ---------- */

    _onPointerEnter() {
      this.isPaused = true;
      this._stopAutoplay();
    }

    _onPointerLeave() {
      this.isPaused = false;
      if (this.autoplayEnabled) {
        this._startAutoplay();
      }
    }

    /* ---------- Auto-Play ---------- */

    _startAutoplay() {
      this._stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.isPaused) {
          this._goToSlide(this.currentIndex + 1, true);
        }
      }, this.autoplayDuration);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    _restartAutoplay() {
      if (this.autoplayEnabled && !this.isPaused) {
        this._startAutoplay();
      }
    }
  }

  if (!customElements.get('pandiswelt-hero-slider')) {
    customElements.define('pandiswelt-hero-slider', PandisweltHeroSlider);
  }
})();
